/**
 * Smooth open/close for FAQ accordions.
 *
 * Enhances every `<details data-smooth>`: clicking its summary animates the
 * height instead of snapping, and the answer fades and settles in. Opening
 * takes 450ms, closing 300ms (exits are faster). Clicking mid-animation
 * reverses smoothly from the current height. While closing, the element
 * carries `data-state="closing"` so the plus icon rotates back right away.
 * Visitors who prefer reduced motion get the native instant toggle.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const OPEN_MS = 450;
const CLOSE_MS = 300;
const animations = new WeakMap();

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const summary = event.target.closest('details[data-smooth] > summary');
  if (!summary || reducedMotion.matches) return;

  const details = /** @type {HTMLDetailsElement} */ (summary.parentElement);
  const answer = summary.nextElementSibling;
  event.preventDefault();

  const wantOpen = details.dataset.state === 'closing' || !details.open;
  const fromHeight = details.getBoundingClientRect().height;

  for (const running of animations.get(details) ?? []) running.cancel();

  // Measure both end states synchronously (no paint in between).
  details.open = false;
  const closedHeight = details.getBoundingClientRect().height;
  details.open = true;
  const openHeight = details.getBoundingClientRect().height;

  if (wantOpen) delete details.dataset.state;
  else details.dataset.state = 'closing';

  details.style.overflow = 'hidden';
  const duration = wantOpen ? OPEN_MS : CLOSE_MS;
  const heightAnimation = details.animate(
    { height: [`${fromHeight}px`, `${wantOpen ? openHeight : closedHeight}px`] },
    { duration, easing: EASING },
  );
  const running = [heightAnimation];

  if (answer instanceof HTMLElement) {
    running.push(
      answer.animate(
        wantOpen
          ? { opacity: [0, 1], transform: ['translateY(-6px)', 'translateY(0)'] }
          : { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-6px)'] },
        wantOpen
          ? { duration: 360, delay: 90, easing: EASING, fill: 'backwards' }
          : { duration: 180, easing: EASING, fill: 'forwards' },
      ),
    );
  }
  animations.set(details, running);

  heightAnimation.onfinish = () => {
    for (const animation of running) animation.cancel();
    animations.delete(details);
    details.style.overflow = '';
    if (!wantOpen) {
      details.open = false;
      delete details.dataset.state;
    }
  };
});
